import React, {useEffect, useState} from "react";

import {DownloadOutlined, DownOutlined, InfoCircleFilled, UpOutlined, WarningFilled} from "@ant-design/icons";
import {Alert, Button, Col, DatePicker, Descriptions, Dropdown, Form, Menu, message, Popover, Radio, Row, Select, Spin, Statistic, Table, Tag, Tooltip} from "antd";
import DescriptionsItem from "antd/es/descriptions/Item";
import MenuDivider from "antd/es/menu/MenuDivider";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";

import axios from "../../api";
import api from "../../api";
import {extractErrorMessage} from "../../index";
import * as Auth from "../../AuthService";
import {
	checkAnyOfPermissions,
	createScopeLink,
	downloadExcel,
	getColumnSearchProps,
	getURLParam,
	labelMapper,
	openLinkInNewTab,
} from "../../helpers";
import {NOT_OPTIMISED_FIELD, projectCurrency, properties, ROP_MIN_LABEL} from "../../properties";
import {DataCard} from "../helpers/cards/Card";
import {GetBack} from "../helpers/cards/MaterialBlock";
import {CustomTabs} from "../helpers/CustomTabs";
import {StandardTable} from "../helpers/TablePanel";
import {ModalWindow} from "../helpers/modals/ModalWindow";
import {BucketOverview} from "../workflows/BucketOverview";
import {BucketsList} from "../workflows/BucketsList";
import {CreateApprovalBucket} from "../workflows/CreateApprovalBucket";
import {FieldsNaming} from "../../variants";

import "../helpers.css";
import {PageTitle} from "../helpers/PageTitle";

const REVIEW_FILTER_OPTIONS = [
	{value: "", label: "All materials"},
	{value: "False", label: "Not reviewed"},
	{value: "True", label: "Reviewed only"},
];

const OPTIMISATION_FILTER_OPTIONS = [
	{value: "", label: "All materials"},
	{value: "True", label: "Yes"},
	{value: "False", label: "No"},
];

const REVIEW_ROLES = ["senior-user", "approve-user", "superadmin"];

export class IOScenarioOverview extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			scenarioInfo: null,
			loading: true,
			loadError: false,
		    archived: new URLSearchParams(window.location.search).get("archived") === "true",
		};
	}

	componentDidMount() {
		this.loadScenarioOverview();
	}

	loadScenarioOverview = () => {
		const project_id = this.props.location.pathname.split("/")[2];
		const iteration_id = this.props.location.pathname.split("/")[5];
		this.setState({loading: true, loadError: false});
		axios
			.get(
			    `/projects/${project_id}/iteration/${iteration_id}/overview?archived=${this.state.archived}`,
			    Auth.createConfig()
		    )
			.then(json_results => {
				this.setState({scenarioInfo: json_results.data, loading: false});
			})
			.catch(error => {
				console.log(error);
				this.setState({loading: false, loadError: true});
			});
	};

	render() {
		const {scenarioInfo, loading, loadError, archived} = this.state;

		if (loading) {
			return (
				<div style={{padding: 50, textAlign: "center"}}>
					<Spin spinning />
				</div>
			);
		}

		if (loadError || !scenarioInfo) {
			const project_id = this.props.location.pathname.split("/")[2];
			const backLink = archived
				? `/projects/${project_id}/min-max?archived=true`
				: `/projects/${project_id}/min-max`;

			return (
				<div style={{padding: 50}}>
					<GetBack link={backLink} style={{marginBottom: 20}} />
					<Alert
						type="error"
						showIcon
						message="Failed to load scenario overview"
						description="The scenario overview could not be loaded. Please try again or go back to the scenarios list."
						action={
							<Button size="small" onClick={this.loadScenarioOverview}>
								Try again
							</Button>
						}
					/>
				</div>
			);
		}

		return (
			<div>
				<PageTitle title={scenarioInfo.iteration_name} />
				<Recommendation scenarioInfo={scenarioInfo} {...this.props} />
			</div>
		);
	}
}

function RecommendationStatsOverview({scenarioInfo}) {
    const [archived] = useState(() => {
        return new URLSearchParams(window.location.search).get("archived") === "true";
    });

	if (!scenarioInfo?.scope || !scenarioInfo?.constants || !scenarioInfo?.results) {
		return null;
	}

	const getDemandSmoothing = constants => {
		if (!constants["use_demand_smoothing"]) {
			return "No";
		} else if (constants["use_demand_smoothing"]) {
			return `Yes (Mode: ${labelMapper(constants["smoothing_mode"])}, Coefficient: ${constants["std_coeff"]})`;
		}
	};

	const exportIteration = () => {
		const hide = message.loading("Preparing Scenario for download...", 0, () => {});

		const project_id = window.document.location.pathname.split("/")[2];
		const iteration_id = window.document.location.pathname.split("/")[5];
		const config = Auth.createConfig();
		config["responseType"] = "blob";
		axios
			.get(
			    `/projects/${project_id}/iterations/${iteration_id}/min-max/export?archived=${archived}`,
			    config
			)
			.then(json => {
				downloadExcel(json.data, `Proposed MIN-MAX ${scenarioInfo.iteration_name}.xlsx`);
			})
			.catch(error => {
				console.log(error);
			})
			.finally(() => setTimeout(hide, 1));
	};

	const stats = {
		scenarioSettings: {
			title: "Scenario Settings",
			stats: [
				{
					label: "Scope Name",
					key: "name",
					source: "scope",
					isLink: true,
					link: (
						<DescriptionsItem labelStyle={{fontWeight: "bold"}} span={3} label={"Scope Name"}>
							<a
								onClick={() =>
									openLinkInNewTab(
										createScopeLink(
											scenarioInfo["scope"]["project_id"],
											scenarioInfo["scope"]["_id"],
										),
									)
								}>
								{scenarioInfo["scope"]["name"]}
							</a>
						</DescriptionsItem>
					),
				},
				{
					label: "Forecast Type",
					key: "forecastingMethod",
					source: "constants",
					isLink: true,
					link: <b>{labelMapper(scenarioInfo["constants"]["forecastingMethod"])}</b>,
				},
				{
					label: "Used transactions",
					key: "use_pm",
					source: "constants",
					isLink: true,
					link: <b>{labelMapper(scenarioInfo["constants"]["use_pm"])}</b>,
				},
				{
					label: "Demand smoothing",
					key: "demand_smoothing",
					source: "constants",
					isLink: true,
					link: <b>{getDemandSmoothing(scenarioInfo["constants"])}</b>,
				},
				{
					label: "Stocking Calculator",
					key: "use_stocking_calculator",
					source: "constants",
					isLink: true,
					link: <b>{scenarioInfo["constants"]["use_stocking_calculator"] ? "Yes" : "No"}</b>,
				},
			],
		},
		records: {
			title: "Scenario Statistics",
			stats: [
				{label: "Total Number of Records", key: "total_number"},
				{label: "Total Number of Records (Has been selected)", key: "total_number_selected"},
				{label: "Total Number of Records (Not selected)", key: "total_number_not_selected"},
			],
			source: "results",
		},
		bestRecommendation: {
			title: "Best Recommendation",
			stats: [
				{label: "Total Predicted GI", key: "total_gi"},
				{label: "Total Predicted Stock-outs", key: "total_so"},
				{label: "Predicted SF", key: "predicted_sl", suffix: "%"},
			],
			source: "results",
		},
		currentRecommendation: {
			title: "Current Recommendation",
			stats: [
				{label: "Total Predicted GI", key: "current_gi"},
				{label: "Total Predicted Stock-outs", key: "current_so"},
				{label: "Predicted SF", key: "current_sl", suffix: "%"},
			],
			source: "results",
		},
		stockValue: {
			title: "Savings",
			stats: [
				{label: "Current MAX Value", key: "current_max_value", suffix: projectCurrency},
				{label: "Proposed MAX Value", key: "proposed_max_value", suffix: projectCurrency},
				{label: "Max Savings", key: "change_max_percentage", suffix: "%"},
			],
			source: "results",
		},
	};

	const createGroupStats = _group =>
		stats[_group]["stats"].map(_stat => (
			<Descriptions.Item span={1} label={_stat["label"]}>
				{_stat["isLink"] ? (
					_stat["link"]
				) : (
					<b>
						<Statistic
							valueStyle={{fontSize: 14}}
							suffix={_stat["suffix"]}
							value={scenarioInfo[_stat["source"] || stats[_group]["source"]][_stat["key"]]}
						/>
					</b>
				)}
			</Descriptions.Item>
		));

	const createFullStats = () => {
		const groups = [];
		for (const _group of Object.keys(stats)) {
			const groupConfig = stats[_group];
			groups.push(
				<div key={_group}>
					{groupConfig.title && (
						<div style={{fontWeight: "bold", marginTop: 8, marginBottom: 4}}>{groupConfig.title}</div>
					)}
					<Descriptions size={"small"}>{createGroupStats(_group)}</Descriptions>
				</div>,
			);
		}
		return groups;
	};

	const renderExceptionStats = () => {
		if (
			!scenarioInfo ||
			!scenarioInfo.ex_report_statistics ||
			Object.keys(scenarioInfo.ex_report_statistics).length === 0
		) {
			return null;
		}

		return (
			<>
				Exceptions:
				<div>
					{Object.entries(scenarioInfo.ex_report_statistics).map(([key, value]) => (
						<div>
							<span>{value.type.charAt(0).toUpperCase() + value.type.slice(1)}:</span>
							<strong> {value.quantity} material(s) with error message </strong>
							<span>{key}</span>
						</div>
					))}
				</div>
			</>
		);
	};

	return (
		<DataCard
			title={
			    <div>
				    <div>
					    Scenario: <b>{scenarioInfo.iteration_name}</b>{" "}
					    {scenarioInfo.iteration_name && (
						    <DownloadOutlined className={"beautiful-link"} onClick={exportIteration} />
					    )}
                    </div>
                    {archived && (
                        <div style={{ color: "red", fontWeight: "bold", marginTop: 2 }}>
                            Scenario was archived by: {scenarioInfo.last_change_by}
                        </div>
                    )}
                </div>
			}>
			{createFullStats()}
			{renderExceptionStats()}
		</DataCard>
	);
}

function RecommendationsIOTable({
	loading = false,
	total = 0,
	pageSize = 10,
	data = [],
	page = 1,
	handleChange,
	sorters = {},
	selectedRowKeys = [],
	setSelectedRowKeys,
	setSelectedRowsByKey,
	editingBucketMode,
	possibleFilterOptions = [],
	menu,
	toolbarExtra,
	onColumnFilterReset,
	filters = {},
	canReview = false,
	onOpenReviewModal,
	onToggleSelectMode,
	onBulkMaterialSearch,
	onBulkMaterialReset,
}) {
	const [state, setState] = useState({});
	const [expandedKeys, setExpandedKeys] = useState([]);
	const [examples, setExamples] = useState([]);
	const [materialsText, setMaterialsText] = useState("");

	useEffect(() => {
		if (!editingBucketMode) {
			setMaterialsText("");
		}
	}, [editingBucketMode]);

	const handleSearch = (selectedKeys, dataIndex, confirm) => {
		confirm();
		setState({searchText: selectedKeys[0]});
		handleChange({current: 1, pageSize: pageSize}, {[dataIndex]: selectedKeys[0]});
	};

	const handleReset = dataIndex => () => {
		setState({searchText: ""});
		onColumnFilterReset(dataIndex);
	};

	const toFilteredValue = value => {
		if (!value) {
			return null;
		}
		return Array.isArray(value) ? value : [value];
	};

	const getOrder = field => (sorters.field === field ? sorters.order : null);

	const columns = [
		{
			key: "material",
			dataIndex: "material",
			title: "Material",
			...getColumnSearchProps("material", true, state, handleReset("material"), handleSearch),
			filteredValue: toFilteredValue(filters.material),
		},
		{
			key: "description",
			dataIndex: "description",
			title: "Description",
			width: "15%",
			ellipsis: true,
			...getColumnSearchProps("description", false, state, handleReset("description"), handleSearch),
			filteredValue: toFilteredValue(filters.description),
			render: text => (
				<Tooltip title={text}>
					<span
						style={{
							display: "inline-block",
							maxWidth: "100%",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{text}
					</span>
				</Tooltip>
			),
		},
		{key: "", dataIndex: "lt_days", title: FieldsNaming.lead_time, width: "5%"},
		{
			key: "recommendation",
			dataIndex: "recommendation",
			title: "Recommendation",
			width: "15%",
			filters: possibleFilterOptions.map(item => ({
				value: item,
				text: item,
			})),
			filteredValue: toFilteredValue(filters.recommendation),
			render: (recommendation, item) => {
				const hasWarning =
					(Object.keys(item).includes("valid_for_optimisation") && !item["valid_for_optimisation"]) ||
					(item.warning && item.warning.length > 0);

				return (
					<>
						{hasWarning ? (
							<Popover
								content={
									<div>{item.warning || `This item was not optimised due to ${NOT_OPTIMISED_FIELD}`}</div>
								}>
								{recommendation}
								<WarningFilled className={"warning"} style={{color: "#faad14", marginLeft: 5}} />
							</Popover>
						) : (
							recommendation
						)}
						{item.is_reviewed && (
							<Popover
								content={
									<div>
										Material is blocked as reviewed from "{item.review_date?.split("T")[0]}" till "
										{item.review_expiry_date?.split("T")[0]}"
									</div>
								}>
								<InfoCircleFilled style={{color: "#1677ff", marginLeft: 5}} />
							</Popover>
						)}
					</>
				);
			},
		},
		{
			title: "Current",
			dataIndex: "current",
			render: (recommendation, item) => (
				<div>
					{item.rop} / {item.max}
				</div>
			),
		},
		{
			title: "Proposed",
			dataIndex: "current",
			render: (recommendation, item) => (
				<div>
					{item.new_rop} / {item.new_max}
				</div>
			),
		},
		{
			title: FieldsNaming.overmax_name,
			dataIndex: "stock_level",
			render: (stock_level, item) => (
				<div>
					<Tooltip title={`Current Stock Level: ${stock_level}`}>
						{stock_level - item.max} / {stock_level - item.new_max}
					</Tooltip>
				</div>
			),
		},
		{
			title: <b>Apply (%)</b>,
			dataIndex: "weight.total.score",
			key: "weight.total.score",
			align: "center",
			render: (totalCost, item) => (item.weight ? item.weight.total.score * 100 : "100"),
		},
		{
			title: <b>Total Cost</b>,
			dataIndex: "total_cost",
			key: "total_cost",
			align: "center",
			render: totalCost => (totalCost ? totalCost : "NA"),
		},
		{
			key: "savings_money",
			dataIndex: "savings_money",
			title: `Saving (${projectCurrency})`,
			width: "5%",
			sorter: true,
			sortOrder: getOrder("savings_money"),
			render: (savings_money, item) => (
				<Popover
					title="Savings"
					content={
						<div>
							<p>
								Current MAX Value ({projectCurrency}): {item.current_max_value}
							</p>
							<p>
								Proposed MAX Value ({projectCurrency}): {item.proposed_max_value}
							</p>
						</div>
					}>
					<Statistic valueStyle={{fontSize: 12}} value={item.current_max_value - item.proposed_max_value} />
				</Popover>
			),
		},
		{
			key: "stockouts",
			dataIndex: "stockouts",
			title: "Stock-Outs",
			width: "5%",
			sorter: true,
			sortOrder: getOrder("stockouts"),
		},
		{
			key: "gi",
			dataIndex: "gi",
			title: "Issues",
			width: "5%",
			sorter: true,
			sortOrder: getOrder("gi"),
		},
		{
			key: "current_info",
			dataIndex: "current_info",
			title: "Current SF",
			width: "5%",
			render: current_info => {
				if (current_info && Object.keys(current_info).includes("sf")) {
					return current_info["sf"];
				} else {
					return "NA";
				}
			},
		},
		{
			key: "service_factor",
			dataIndex: "sf",
			title: "Proposed SF",
			width: "5%",
			render: (sf, item) => {
				if (item["current_info"] && Object.keys(item["current_info"]).includes("sf")) {
					const increased = sf >= item["current_info"]["sf"];
					return <div style={{color: increased ? "green" : "red"}}>{sf}</div>;
				} else {
					return <div>{sf}</div>;
				}
			},
		},
		{
			key: "use_pm",
			dataIndex: "use_pm",
			title: "Use PM Demand",
			width: "7%",
			render: use_pm => {
				if (typeof use_pm === "boolean") {
					return use_pm ? "Yes" : "No";
				} else {
					return labelMapper(use_pm);
				}
			},
		},
		{
			key: "exceptions",
			dataIndex: "exceptions",
			title: "Exceptions",
			render: hasException => (typeof hasException === "boolean" ? (hasException ? "Yes" : "No") : "N/A"),
		},
	];

	const getExamples = record => {
		const project_id = window.document.location.pathname.split("/")[2];

		if (!Object.keys(record).indexOf("plant_info") > -1) {
			api.post(`/projects/${project_id}/material-analysis/${record.material}/stockAllocation`, {
				min: record.new_rop,
				max: record.new_max,
			}).then(response => {
				record.plant_info = response.data;
				setExamples(response.data);
			});
		}
	};

	const onTableRowExpand = (expanded, record) => {
		const keys = [];
		if (expanded) {
			keys.push(record.key);
		}
		setState({...state, expandedRowKeys: keys});
	};

	const getNestedTable = record => (
		<Table
			className="nested-table-allocation"
			bordered={true}
			pagination={false}
			dataSource={examples || []}
			columns={nestedColumns()}
		/>
	);

	const nestedColumns = () => [
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
		},
		{
			title: "MRP Type",
			dataIndex: "MRP Type",
			key: "plant",
		},
		{
			title: `Plant ${ROP_MIN_LABEL}`,
			dataIndex: "rop",
			key: "plant",
		},
		{
			title: "Plant MAX",
			dataIndex: "max",
			key: "plant",
		},
		{
			title: `Proposed ${ROP_MIN_LABEL}`,
			dataIndex: "appr_min",
			key: "plant",
		},
		{
			title: "Proposed MAX",
			dataIndex: "appr_max",
			key: "plant",
		},
		{
			title: "Plant Stock",
			dataIndex: "plant_stock_level",
			key: "plant",
		},
		{
			title: "Plant price",
			dataIndex: "plant_price",
			key: "plant",
		},
		{
			title: FieldsNaming.plant_lead_time,
			dataIndex: "plant_lead_time",
			key: "plant",
		},
		{
			title: "Plant Installed",
			dataIndex: "quantity",
			key: "plant",
		},
	];

	const rowSelection = {
		selectedRowKeys,
		preserveSelectedRowKeys: true,
		onChange: (keys, rows) => {
			setSelectedRowKeys(keys);
			setSelectedRowsByKey(prev => {
				const next = {...prev};
				rows.forEach(row => {
					next[row.key] = row;
				});
				Object.keys(next).forEach(key => {
					if (!keys.includes(key)) {
						delete next[key];
					}
				});
				return next;
			});
		},
		getCheckboxProps: record => ({
			disabled: record.bucket_id,
			name: record.bucket_id,
		}),
	};

	const getClassName = row => {
		if (row.approved) {
			return "approved";
		}

		if (row.bucket_id) {
			return "bucket";
		}

		return "";
	};

	return (
		<DataCard>
			<div>
				<div
					style={{
						marginBottom: 16,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}>
					<div>
						<Button type="primary" onClick={onToggleSelectMode}>
							Select materials
						</Button>
						<Dropdown overlay={menu}>
							<Button style={{margin: "0 10px"}} loading={loading}>
								Add to Bucket
							</Button>
						</Dropdown>
						{canReview && (
							<Button
								style={{marginRight: 10}}
								disabled={!editingBucketMode || selectedRowKeys.length === 0}
								onClick={onOpenReviewModal}>
								Mark as reviewed
							</Button>
						)}

						<span
							style={{
								marginLeft: 8,
							}}>
							{editingBucketMode ? `Selected ${selectedRowKeys.length || 0} items` : ""}
						</span>
					</div>
					{toolbarExtra}
				</div>
				{editingBucketMode && (
					<div style={{marginBottom: 16, maxWidth: 480}}>
						<TextArea
							rows={4}
							value={materialsText}
							placeholder={"10101010\n10101020"}
							onChange={event => setMaterialsText(event.target.value)}
						/>
						<div style={{marginTop: 8}}>
							<Button
								type="primary"
								onClick={() => {
									const materials = [
										...new Set(
											materialsText
												.split(/[\n,]+/)
												.map(item => item.trim())
												.filter(Boolean),
										),
									];
									if (!materials.length) {
										message.error("No valid materials found!");
										return;
									}
									onBulkMaterialSearch(materials);
								}}>
								Search
							</Button>
							{filters.material_list && (
								<Button
									style={{marginLeft: 8}}
									onClick={() => {
										setMaterialsText("");
										onBulkMaterialReset();
									}}>
									Reset
								</Button>
							)}
							<span style={{marginLeft: 8}}>Separate materials by new line or comma</span>
						</div>
					</div>
				)}
			</div>

			<StandardTable
				size={"small"}
				loading={loading}
				total={total}
				currentPage={page}
				columns={columns}
				scroll={350}
				tableProps={{
					rowKey: "material",
					rowClassName: record => getClassName(record),
					expandIconColumnIndex: 16,
					expandedRowKeys: expandedKeys,
					expandable: {
						expandedRowRender: record => (
							<p
								style={{
									margin: 0,
								}}>
								{getNestedTable(record)}
							</p>
						),
						onExpand: (expanded, record) => {
							if (expanded) {
								setExpandedKeys([record.key]);
								getExamples(record);
							} else {
								setExpandedKeys([]);
							}
						},
						expandIcon: ({expanded, onExpand, record}) =>
							expanded ? (
								<UpOutlined onClick={e => onExpand(record, e, expanded)} />
							) : (
								<DownOutlined onClick={e => onExpand(record, e, expanded)} />
							),
						rowExpandable: record => record.material,
					},
					rowSelection: editingBucketMode ? rowSelection : null,
				}}
				onChange={handleChange}
				pageSize={pageSize}
				data={data}
			/>
		</DataCard>
	);
}

function Recommendation({scenarioInfo}) {
	const defaultSort = {
		field: "savings_money",
		order: "descend",
	};

	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [data, setData] = useState([]);
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({});
	const [sorters, setSorters] = useState(defaultSort);
	const [buckets, setBuckets] = useState({items: [], total: 0});
	const [addingBucket, setAddingBucket] = useState(false);
	const [activeTab, setActiveTab] = useState("items");
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [selectedRowsByKey, setSelectedRowsByKey] = useState({});
	const [editingBucketMode, setEditingBucketMode] = useState(false);
	const [canReview, setCanReview] = useState(false);
	const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
	const [reviewForm] = Form.useForm();
	const [recommendationsFilter, setRecommendationsFilter] = useState([]);
	const [archived] = useState(() => {
        return new URLSearchParams(window.location.search).get("archived") === "true";
    });

	const clearSelection = () => {
		setSelectedRowKeys([]);
		setSelectedRowsByKey({});
	};

	const toggleSelectMode = () => {
		if (editingBucketMode && filters.material_list) {
			setFilters(prev => {
				const next = {...prev};
				delete next.material_list;
				return next;
			});
		}
		setEditingBucketMode(!editingBucketMode);
	};

	const onBulkMaterialReset = () => {
		clearSelection();
		setFilters(prev => {
			if (!prev.material_list) {
				return prev;
			}
			const next = {...prev};
			delete next.material_list;
			return next;
		});
	};

	const onBulkMaterialSearch = materials => {
		const nextFilters = {...filters, material_list: materials};
		const project_id = window.document.location.pathname.split("/")[2];
		const iteration_id = window.document.location.pathname.split("/")[5];
		clearSelection();
		setPage(1);
		setFilters(nextFilters);
		axios
			.post(
				`/projects/${project_id}/iterations/${iteration_id}/min-max?page=1&size=${materials.length}&archived=${archived}`,
				{
					_search: "",
					_filters: nextFilters,
					sorters: prepareSorters(),
				},
				Auth.createConfig(),
			)
			.then(json => {
				const selectable = json.data.items.filter(item => !item.bucket_id);
				const selected = {};
				selectable.forEach(item => {
					selected[item.material] = item;
				});
				setSelectedRowKeys(selectable.map(item => item.material));
				setSelectedRowsByKey(selected);
				const found = new Set(json.data.items.map(item => item.material));
				const missingCount = materials.filter(material => !found.has(material)).length;
				if (missingCount) {
					message.info(
						`${selectable.length} materials selected. ${missingCount} not found in this scenario.`,
					);
				}
			})
			.catch(error => {
				console.log(error);
			});
	};

	const getSelectedRows = () => selectedRowKeys.map(key => selectedRowsByKey[key]).filter(Boolean);

	const openReviewModal = () => {
		reviewForm.resetFields();
		reviewForm.setFieldsValue({
			review_expiry_date: dayjs().add(6, "month"),
			skip_reviewed: false,
		});
		setIsReviewModalVisible(true);
	};

	const closeReviewModal = () => setIsReviewModalVisible(false);

	const onReviewSubmit = values => {
		setLoading(true);
		api.post(`/projects/${scenarioInfo.project_id}/materials/review`, {
			materials: selectedRowKeys,
			review_expiry_date: values.review_expiry_date.format("YYYY-MM-DD"),
			notes: values.notes,
			skip_reviewed: values.skip_reviewed,
		})
			.then(response => {
				const {updated, skipped} = response.data;
				if (updated === 0) {
					message.warning("No materials were updated. All selected materials are already reviewed.");
				} else if (skipped > 0) {
					message.success(`${updated} material(s) marked as reviewed. ${skipped} already reviewed material(s) skipped.`);
				} else {
					message.success(`${updated} material(s) marked as reviewed.`);
				}
				closeReviewModal();
				clearSelection();
				setEditingBucketMode(false);
				getIterationResults();
			})
			.catch(error => message.error(error.response?.data?.msg || "Failed to mark as reviewed."))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		checkAnyOfPermissions(REVIEW_ROLES)
			.then(() => setCanReview(true))
			.catch(() => setCanReview(false));
	}, []);

	const getIterationResults = (_page = page, size = pageSize, _filters = filters) => {
		if (!loading) {
			const project_id = window.document.location.pathname.split("/")[2];
			const iteration_id = window.document.location.pathname.split("/")[5];
			setLoading(true);
			axios
				.post(
					`/projects/${project_id}/iterations/${iteration_id}/min-max?page=${_page}&size=${size}&archived=${archived}`,
					{
						_search: "",
						_filters: _filters,
						sorters: prepareSorters(),
					},
					Auth.createConfig(),
				)
				.then(json => {
					for (const item of json.data.items) {
						item.key = item.material;
					}
					setData(json.data.items);
					setTotal(json.data.total);
					setPage(_page);
					setPageSize(size);
				})
				.catch(error => {
					console.log(error);
				})
				.finally(() => setLoading(false));
		}
	};

	const prepareSorters = () => {
		const sorter = {};

		sorter[sorters["field"]] = sorters["order"] === "ascend" ? 1 : -1;

		return sorter;
	};

	const handleTableChange = (newPagination, tableFilters = null, sorter = null) => {
		setPage(newPagination.current);
		setPageSize(newPagination.pageSize);
		if (tableFilters) {
			setFilters(prev => {
				const columnFilters = {...prev};
				for (const [key, value] of Object.entries(tableFilters)) {
					if (value?.length) {
						columnFilters[key] = value;
					} else {
						delete columnFilters[key];
					}
				}
				return columnFilters;
			});
		}

		if (sorter) {
			setSorters(sorter);
		} else {
			setSorters(defaultSort);
		}
	};

	const handleColumnFilterReset = dataIndex => {
		setFilters(prev => {
			const next = {...prev};
			delete next[dataIndex];
			return next;
		});
		clearSelection();
		setPage(1);
	};

	const getBucketsList = () => {
		const project_id = window.document.location.pathname.split("/")[2];
		const iteration_id = window.document.location.pathname.split("/")[5];

		api.get(`/projects/${project_id}/scenarios/${iteration_id}/approval-buckets`)
			.then(json => {
				setBuckets(json.data);
			})
			.catch(error => {
				console.log(error);
			});
	};

	useEffect(() => {
		getIterationResults();
	}, [page, sorters, buckets, filters]);

	useEffect(() => {
		getIterationResults();
		getBucketsList();
		const bucketId = getURLParam("bucket_id");
		if (bucketId) {
			setActiveTab(bucketId);
		}
	}, [scenarioInfo]);

	useEffect(() => {
		setPage(1);
	}, [pageSize, filters, sorters]);

	const addMaterialsToBucket = bucketId => {
		api.post(`/projects/${scenarioInfo.project_id}/approval-buckets/${bucketId}/materials`, {
			material_identifiers: getSelectedRows().map(material => material._id),
		})
			.then(response => {
				clearSelection();
				getBucketsList();
				setEditingBucketMode(false);
			})
			.catch(error => {})
			.finally(() => setLoading(false));
	};

	const menu = (
		<Menu>
			{buckets?.items.map(item => (
				<Menu.Item
					disabled={!editingBucketMode || selectedRowKeys.length === 0}
					onClick={() => addMaterialsToBucket(item._id)}
					key={item.id}>
					Add to {item.name}
				</Menu.Item>
			))}
			<MenuDivider />
			<Menu.Item
				disabled={!editingBucketMode || selectedRowKeys.length === 0}
				onClick={() => createNewBucket()}
				key={"new-bucket"}>
				Create new Bucket
			</Menu.Item>
		</Menu>
	);

	const createNewBucket = () => {
		setAddingBucket(true);
	};

	const onApprovalStart = bucket_id => {
		api.post(`/approval-buckets/${bucket_id}/start`, {}, {omitGlobalError: true})
			.then(response => {
				console.log(response);
				message.success("Successfully started Approval process for the Bucket");
				getBucketsList();
			})
			.catch(async error => {
				message.error(await extractErrorMessage(error), 10);
			});
	};

	const getOptionsForFilter = () =>
		scenarioInfo?.results?.recommendation_stats?.map(key => key.recommendation) ?? [];

	useEffect(() => {
		setRecommendationsFilter(getOptionsForFilter());
	}, [scenarioInfo?.results]);

	const getTabs = () => {
		const tabs = [
			{
				name: "Scenario Outcome",
				key: "items",
				children: (
					<RecommendationsIOTable
						handleChange={handleTableChange}
						data={data}
						loading={loading}
						total={total}
						page={page}
						editingBucketMode={editingBucketMode}
						onToggleSelectMode={toggleSelectMode}
						onBulkMaterialSearch={onBulkMaterialSearch}
						onBulkMaterialReset={onBulkMaterialReset}
						menu={menu}
						selectedRowKeys={selectedRowKeys}
						setSelectedRowKeys={setSelectedRowKeys}
						setSelectedRowsByKey={setSelectedRowsByKey}
						canReview={canReview}
						onOpenReviewModal={openReviewModal}
						pageSize={pageSize}
						sorters={sorters}
						possibleFilterOptions={recommendationsFilter}
						toolbarExtra={
							<ScenarioOutcomeAddFilterPopover
								reviewValue={filters.is_reviewed?.[0] ?? ""}
								optimisationValue={filters.valid_for_optimisation?.[0] ?? ""}
								onApply={({reviewed, optimised}) => {
									clearSelection();
									setFilters(prev => {
										const next = {...prev};
										if (reviewed) {
											next.is_reviewed = [reviewed];
										} else {
											delete next.is_reviewed;
										}
										if (optimised) {
											next.valid_for_optimisation = [optimised];
										} else {
											delete next.valid_for_optimisation;
										}
										return next;
									});
									setPage(1);
								}}
							/>
						}
						onColumnFilterReset={handleColumnFilterReset}
						filters={filters}
					/>
				),
			},
			{
				name: `Recommendations Summary`,
				key: "recommendations",
				children: <RecommendationsCombinedStats scenarioInfo={scenarioInfo} />,
			},
			{
				name: `Approval Buckets List (${buckets.total})`,
				key: "bucketList",
				children: (
					<BucketsList
						buckets={buckets}
						onDelete={onDelete}
						onAddBucket={onAddBucket}
						openBucket={openBucket}
						onApprovalStart={onApprovalStart}
					/>
				),
			},
		];

		for (const bucket of buckets.items) {
			tabs.push({
				name: `${bucket.name} (${bucket.material_count} Materials)`,
				key: bucket._id,
				children: (
					<BucketOverview
						bucket={bucket}
						onApprovalStart={onApprovalStart}
						projectId={scenarioInfo.project_id}
						scenarioId={scenarioInfo._id}
						refresh={() => getBucketsList()}
					/>
				),
			});
		}

		return tabs;
	};

	const openBucket = bucketId => {
		console.log(`open bucket: ${bucketId}`);
		setActiveTab(bucketId);
	};

	const onDelete = bucketId => {
		api.delete(`/projects/${scenarioInfo.project_id}/approval-buckets/${bucketId}`)
			.then(json => {
				message.success("Bucket Deleted!");
				getBucketsList();
			})
			.catch(error => {});
	};

	const onAddBucket = () => {
		setAddingBucket(true);
	};

	const onClose = () => {
		setAddingBucket(false);
		setEditingBucketMode(false);
	};

	return (
		<div style={{padding: 5}}>
			<Row>
				<Col span={2} />
				<Col span={20}>
					<RecommendationStatsOverview scenarioInfo={scenarioInfo} />
				</Col>
			</Row>
			<Row style={{marginTop: 10, height: "calc(100% - 600px)"}}>
				<CustomTabs tabsData={getTabs()} onChange={setActiveTab} activeKey={activeTab} />
				<CreateApprovalBucket
					addingBucket={addingBucket}
					onClose={value => onClose(value)}
					projectId={scenarioInfo.project_id}
					scenarioId={scenarioInfo._id}
					refresh={getBucketsList}
					selectedKeys={getSelectedRows()}
					setSelectedKeys={clearSelection}
				/>
				<ModalWindow
					modalProps={{maxHeight: "40vh"}}
					title={"Mark as reviewed"}
					isModalVisible={isReviewModalVisible}
					onOk={() => reviewForm.submit()}
					onCancel={closeReviewModal}>
					<Form onFinish={onReviewSubmit} form={reviewForm} layout="vertical">
						<Form.Item
							label={"Review expiry date"}
							name={"review_expiry_date"}
							rules={[
								{required: true},
								{
									validator: (_, value) =>
										value && value.isBefore(dayjs(), "day")
											? Promise.reject(new Error("Expiry date cannot be in the past"))
											: Promise.resolve(),
								},
							]}>
							<DatePicker
								style={{width: "100%"}}
								disabledDate={current => current && current < dayjs().startOf("day")}
							/>
						</Form.Item>
						<Form.Item label={"Notes"} name={"notes"}>
							<TextArea rows={4} />
						</Form.Item>
						<Form.Item label={"Already reviewed materials"} name={"skip_reviewed"}>
							<Radio.Group>
								<Radio value={false}>Update review date and notes</Radio>
								<Radio value={true}>Skip</Radio>
							</Radio.Group>
						</Form.Item>
					</Form>
				</ModalWindow>
			</Row>
		</div>
	);
}

function RecommendationsCombinedStats({scenarioInfo}) {
	const recommendationStats = scenarioInfo?.results?.recommendation_stats ?? [];
	const totalNumber = scenarioInfo?.results?.total_number ?? 0;

	const countPercentage = (_number, _total) => (_total ? Math.round((_number / _total) * 100) : 0);

	const columns = [
		{
			title: "Recommendation",
			dataIndex: "recommendation",
		},
		{
			title: "Count",
			dataIndex: "count",
			render: count => (
				<div>
					<Statistic
						value={count}
						valueStyle={{fontSize: 14}}
						suffix={`/ ${totalNumber} (${countPercentage(count, totalNumber)} %)`}
					/>
				</div>
			),
		},
	];

	if (!recommendationStats.length) {
		return null;
	}

	return (
		<DataCard>
			<StandardTable size={"small"} columns={columns} scroll={350} data={recommendationStats} />
		</DataCard>
	);
}

function ScenarioOutcomeAddFilterPopover({reviewValue, optimisationValue, onApply}) {
	const [open, setOpen] = useState(false);
	const [reviewed, setReviewed] = useState(reviewValue);
	const [optimised, setOptimised] = useState(optimisationValue);

	const handleOpenChange = nextOpen => {
		if (nextOpen) {
			setReviewed(reviewValue);
			setOptimised(optimisationValue);
		}
		setOpen(nextOpen);
	};

	const apply = () => {
		onApply({
			reviewed: reviewed || null,
			optimised: optimised || null,
		});
		setOpen(false);
	};

	return (
		<Popover
			open={open}
			onOpenChange={handleOpenChange}
			placement="bottomRight"
			trigger="click"
			title="Add filter"
			content={
				<div style={{width: 240}}>
					<div style={{marginBottom: 4}}>Reviewed</div>
					<Select style={{width: "100%"}} value={reviewed} onChange={setReviewed} options={REVIEW_FILTER_OPTIONS} />
					<div style={{marginTop: 12, marginBottom: 4}}>Potentially Optimised</div>
					<Select
						style={{width: "100%"}}
						value={optimised}
						onChange={setOptimised}
						options={OPTIMISATION_FILTER_OPTIONS}
					/>
					<div style={{marginTop: 12, textAlign: "right"}}>
						<Button size="small" onClick={() => setOpen(false)}>
							Cancel
						</Button>
						<Button size="small" type="primary" onClick={apply} style={{marginLeft: 8}}>
							Add
						</Button>
					</div>
				</div>
			}>
			<Button size="small" icon={<img src={process.env.PUBLIC_URL + "/images/filter_list-24px.svg"} alt="" />}>
				Add Filter
			</Button>
		</Popover>
	);
}
